import React, { useState, useEffect, useRef } from "react";
import styles from "./DeviceManager.module.scss";
import { Button, Card, Col, Form, Modal, Row, Spinner, Container } from "react-bootstrap";
import { t } from "i18next";
import DataManager from "../../../General/DataManager";
import UserClass from "../../../General/LocalStorage/UserClass";
import Functions from "../../../General/Functions";
import TablePagination from "../../../Layouts/TablePagination/TablePagination";

const DEFAULT_LIMIT = 10;
const MIN_SEARCH_LENGTH = 2;

const DeviceManager = () => {
    const [applications, setApplications] = useState([]);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [showModal, setShowModal] = useState(true);
    const [devices, setDevices] = useState([]);
    const [itemsPagination, setItemsPagination] = useState(null);
    const [deviceUserSelected, setDeviceUserSelected] = useState("");
    const [showEditModal, setShowEditModal] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [DeviceName, setDeviceName] = useState("");
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [showSpinner, setShowSpinner] = useState(true);
    const [searchValue, setSearchValue] = useState("");
    // Search term actually sent to the API (debounced, min 3 chars)
    const [appliedSearch, setAppliedSearch] = useState("");
    const [loadingDevices, setLoadingDevices] = useState(false);
    const [currentPage, setCurrentPage] = useState(null);
    const searchTimeout = useRef(null);
    const requestIdRef = useRef(0);

    useEffect(() => () => clearTimeout(searchTimeout.current), []);

    useEffect(() => {
        const instance = UserClass.getInstance();
        const userToken = instance.getTokenUser();
        getDeviceApplications(userToken);
    }, []);

    const getDeviceApplications = (token) => {
        DataManager.getApplicationList(token).then((response) => {
            const temp = response?.result?.results || [];
            if (response?.result) {
                const instance = UserClass.getInstance();
                const userInfo = instance.getUser();
                setCompanies(userInfo.companies || []);

                if (userInfo?.role === "SYSADMIN" || userInfo?.role === "DEVICE_MANAGER") {
                    temp.push("TUTTE");
                }
                setApplications(temp);
            } else {
                Functions.alertMsg(response?.msgError || t("msgErrorGeneral"));
            }
        });
    };

    const setApplication = (e) => {
        const app = applications[e.target.selectedIndex - 1];
        clearTimeout(searchTimeout.current);
        setSearchValue("");
        setAppliedSearch("");

        if (app) {

            setTimeout(() => {
                setSelectedApplication(app);
                loadDevices({ app, page: 1, name: "" });
                setShowModal(false);
            }, 300);
        }
        else {
            // Ignore responses still in flight for the previous application
            requestIdRef.current++;
            setSelectedApplication(null);
            setShowModal(true);

        }
    };

    // Single entry point for the list: application, search, page and page size always travel together
    const loadDevices = ({
        app = selectedApplication,
        page = 1,
        limit = itemsPagination?.limit || DEFAULT_LIMIT,
        name = appliedSearch,
    } = {}) => {
        const application = app === "TUTTE" ? "" : app;
        const instance = UserClass.getInstance();
        const token = instance.getTokenUser();
        const requestId = ++requestIdRef.current;

        setLoadingDevices(true);
        DataManager.getDevicesListPagination(token, application, page, limit, true, name).then((response) => {
            // A newer request (e.g. the user kept typing) supersedes this one
            if (requestId !== requestIdRef.current) return;
            setLoadingDevices(false);

            if (response?.result) {
                const data = response.result;
                setDevices(data.results || []);
                setItemsPagination(data.pagination);
                setCurrentPage(data.pagination?.page);
            } else {
                Functions.alertMsg(response?.msgError || t("msgErrorGeneral"));
            }
        });
    };

    const renderColumnTable = () => (
        <thead>
            <tr>
                <th>{t("device_name")}</th>
                <th>{t("udid")}</th>
                <th>{t("application")}</th>
                <th>{t("company")}</th>
                <th>{t("actions")}</th>
            </tr>
        </thead>
    );

    const handleEdit = (item) => {
        setShowEditModal(true);
        setDeviceUserSelected(item);
        setSelectedCompanies(item.companies);
        setDeviceName(item.name);
    };

    const renderActionSection = (item) => (
        <td key={item.id}>
            <div className="text-center">
                <Button
                    variant="outline-primary"
                    size="sm"
                    className="rounded-pill"
                    onClick={() => handleEdit(item)}
                >
                    <i className="fa fa-edit me-2"></i>
                    {t("edit")}
                </Button>
            </div>
        </td>
    );

    const renderListDevices = () => {
        //const data = searchValue !== "" ? filter : devices;
        const data = devices;
        return (
            <tbody>
                {!loadingDevices && data.length === 0 && (
                    <tr>
                        <td colSpan={5} className="text-center text-muted py-4">
                            {appliedSearch
                                ? `${t("no_devices_found")} "${appliedSearch}"`
                                : t("no_devices_found")}
                        </td>
                    </tr>
                )}
                {data.map((item, idx) => (
                    <tr key={idx}>
                        <td>{item.name}</td>
                        <td>{item.udid}</td>
                        <td>{item.application}</td>
                        <td>
                            {item.companies.map((companyName, index) => (
                                <Form.Check
                                    key={index}
                                    type="checkbox"
                                    label={companyName}
                                    checked
                                    disabled
                                />
                            ))}
                        </td>
                        {renderActionSection(item)}
                    </tr>
                ))}
            </tbody>
        );
    };

    const renderPagination = () => {
        // Pagination always reflects the applied search, so it stays visible while searching
        if (itemsPagination) {
            return (
                <TablePagination
                    page={itemsPagination.page}
                    totalPages={Math.max(1, itemsPagination.totalPages)}
                    totalResults={itemsPagination.totalResult ?? itemsPagination.total ?? 0}
                    limit={itemsPagination.limit}
                    onPageChange={(number) => loadDevices({ page: number })}
                    onLimitChange={(size) => loadDevices({ page: 1, limit: size })}
                />
            );
        }
        return null;
    };


    const handleDeviceUpdate = () => {
        if (deviceUserSelected.application === "CHECKIN" && selectedCompanies.length > 1) {
            Functions.alertMsg(t("onlyone"));
            return;
        }
        if (selectedCompanies.length === 0) {
            Functions.alertMsg(t("selectonecom"));
            return;
        }
        if (DeviceName === "") {
            Functions.alertMsg(t("devicenameerror"));
            return;
        }

        setShowSpinner(false);
        const instance = UserClass.getInstance();
        const token = instance.getTokenUser();
        const data = { name: DeviceName, companies: selectedCompanies, id: deviceUserSelected.id };

        DataManager.updateDeviceDetails(token, data).then((response) => {
            if (response?.result) {
                Functions.alertMsgSuccess(t("save_success"));
                loadDevices({ page: currentPage || 1 });
                setShowSpinner(true);
                handleClose();
            } else {
                Functions.alertMsg(response?.msgError || t("msgErrorGeneral"));
            }
        });
    };

    const handleClose = () => setShowEditModal(false);
    const handleDeviceNameChange = (event) => setDeviceName(event.target.value);

    /*const handleSearch2 = (e) => {
        const newValue = e.target.value;
        setSearchValue(newValue);
        if (newValue.length > 3) {
            getListDevices(selectedApplication, newValue);
        }
        else if (newValue === "") {

            getListDevices(selectedApplication);

        }
    }*/
    const handleSearch2 = (e) => {
        const newValue = e.target.value;
        setSearchValue(newValue);

        clearTimeout(searchTimeout.current);

        // Under the minimum length the list is unfiltered
        const trimmed = newValue.trim();
        const term = trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : "";
        if (term === appliedSearch) return;

        searchTimeout.current = setTimeout(() => {
            setAppliedSearch(term);
            loadDevices({ page: 1, name: term });
        }, 300);
    };

    const handleSelectedCompany = (event) => {
        if (deviceUserSelected.application === "CHECKIN") {
            setSelectedCompanies([event.target.value]);
        } else {
            const { value, checked } = event.target;
            setSelectedCompanies((prev) =>
                checked ? [...prev, value] : prev.filter((company) => company !== value)
            );
        }
    };

    return (
        <div className={styles.DeviceManager}>
            <Container fluid="md">
                <Card className="p-4 shadow-sm">
                    <Row className="mb-3">
                        <Col xs={12} md={6}>
                            <Form.Label className="fw-semibold">{t("selectapplication")}</Form.Label>
                            <Form.Select size="lg" onChange={setApplication}>
                                <option key="value_null" value={null}>-- {t("select")} --</option>
                                {applications?.map((item, idx) => (
                                    <option key={idx}>{item}</option>
                                ))}
                            </Form.Select>
                        </Col>
                    </Row>

                    {!showModal && (
                        <>
                            <Row className="mb-4">
                                <Col xs={12} md={6}>
                                    <Form.Label className="fw-semibold">
                                        {t("search")}
                                        {loadingDevices && <Spinner animation="border" size="sm" className="ms-2" />}
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        id="search"
                                        value={searchValue}
                                        //onChange={handleSearch}
                                        onChange={handleSearch2}
                                        placeholder={"🔍" + t('search_by_name_udid')}
                                    />
                                    {searchValue.trim() !== "" && searchValue.trim().length < MIN_SEARCH_LENGTH && (
                                        <Form.Text className="text-muted">
                                            {t("search_min_chars")}
                                        </Form.Text>
                                    )}
                                </Col>
                            </Row>

                            <div className="table-responsive">
                                <table className="table align-middle table-striped table-hover">
                                    {renderColumnTable()}
                                    {renderListDevices()}
                                </table>
                            </div>

                            <div className="mt-3">{renderPagination()}</div>
                        </>
                    )}
                </Card>
            </Container>

            <Modal show={showEditModal} onHide={handleClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t("update_device")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>{t("device_name")}</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={t("device_name")}
                                value={DeviceName}
                                onChange={handleDeviceNameChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>{t("company")}</Form.Label>
                            {companies?.map((companyName, index) => (
                                <Form.Check
                                    key={index}
                                    type="checkbox"
                                    id={companyName}
                                    label={companyName}
                                    value={companyName}
                                    checked={selectedCompanies?.includes(companyName)}
                                    onChange={handleSelectedCompany}
                                />
                            ))}
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Spinner hidden={showSpinner} animation="border" size="sm" />
                    <Button variant="secondary" onClick={handleClose}>
                        {t("close")}
                    </Button>
                    <Button variant="primary" onClick={handleDeviceUpdate}>
                        {t("apply")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default DeviceManager;
